/**
 * Effective status for service-provider connections backed by an OAuth connection.
 *
 * Such a connection is a holder: it carries no credential of its own, only the
 * resource id of the OAuth connection that supplies one. Its usable status is
 * therefore the status of that OAuth connection, which lives in the data-plane
 * connection list rather than in connections.json.
 */
import type { AvailableConnection, ConnectionReference, ConnectionStatus } from '@/types/connectionReference';
import type { ConnectionsConfiguration } from '@/types/logicApps';

/** The two shapes a holder arrives in: a store reference, or a raw connections.json entry. */
type OAuthConnectionHolder = {
  connectionProperties?: Record<string, unknown> | undefined;
  parameterValues?: Record<string, unknown> | undefined;
};

function readOAuthConnectionId(
  values: Record<string, unknown> | undefined,
  parameterKey: string,
): string | undefined {
  const declared = Object.entries(values ?? {}).find(
    ([key]) => key.toLowerCase() === parameterKey.toLowerCase(),
  );
  const value = declared?.[1];

  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

/**
 * Resource id of the OAuth connection backing `holder`, or `undefined` when the
 * holder is an ordinary connection that carries its own credential.
 *
 * `parameterKey` is the name the connector's parameter set gives its
 * `oauthConnection` parameter — see `getOAuthConnectionParameterKey`.
 */
export function getOAuthConnectionId(
  holder: OAuthConnectionHolder | ConnectionReference | null | undefined,
  parameterKey: string,
): string | undefined {
  if (!holder) return undefined;

  const source = holder as OAuthConnectionHolder;

  return (
    readOAuthConnectionId(source.connectionProperties, parameterKey) ??
    readOAuthConnectionId(source.parameterValues, parameterKey)
  );
}

/**
 * Effective status of an OAuth-backed connection, for a holder already known to use an
 * `oauthConnection` parameter set.
 *
 * No id recorded, an id pointing at a connection that is not in `apiConnections`, and an
 * `apiConnections` that has not loaded all report `error` — they are the same fact, that
 * there is no usable sign-in. This drives the designer picker, where withholding a holder
 * that turns out to be fine costs a moment's wait and offering one that turns out to be
 * dead costs a failed run. Surfaces that would rather say "not known yet" — the Connections
 * blade, for one — must distinguish those cases before calling this.
 */
export function resolveOAuthConnectionStatus(
  holder: OAuthConnectionHolder | ConnectionReference | null | undefined,
  apiConnections: AvailableConnection[] | undefined,
  parameterKey: string,
): ConnectionStatus {
  const oauthConnectionId = getOAuthConnectionId(holder, parameterKey);
  if (!oauthConnectionId) return 'error';

  const target = oauthConnectionId.toLowerCase();
  const oauthConnection = (apiConnections ?? []).find(
    (connection) => connection.id?.toLowerCase() === target,
  );

  return oauthConnection?.status ?? 'error';
}

/**
 * Drop every service-provider holder in `config` that points at `oauthConnectionId`.
 *
 * The sign-in is a single app-scoped resource, so it cannot be deleted for one holder
 * and kept for another. Deleting any holder therefore takes its siblings with it.
 * Returns `undefined` when the config has no such holder, so callers can skip the save.
 */
export function removeOAuthConnectionHolders(
  config: ConnectionsConfiguration | undefined,
  oauthConnectionId: string,
  parameterKey: string,
): { config: ConnectionsConfiguration; removedKeys: string[] } | undefined {
  const target = oauthConnectionId.toLowerCase();
  const holders = config?.serviceProviderConnections;
  if (!config || !holders) return undefined;

  const removedKeys = Object.keys(holders).filter(
    (key) =>
      getOAuthConnectionId(holders[key] as OAuthConnectionHolder, parameterKey)?.toLowerCase() ===
      target,
  );
  if (removedKeys.length === 0) return undefined;

  const next = { ...holders };
  for (const key of removedKeys) delete next[key];

  return { config: { ...config, serviceProviderConnections: next }, removedKeys };
}
